import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Accordion from '../components/accordion';

gsap.registerPlugin(ScrollTrigger);

export default class Hero {
    constructor(query) {
        const el = document.querySelector(query);
        if (!el) return;

        this.el = el;

        this.inner = this.el.querySelector('.hero__inner');
        this.introScene = this.el.querySelector('.hero__scene-intro');
        this.advantagesScene = this.el.querySelector('.hero__scene-advantages');
        this.aboutScene = this.el.querySelector('.hero__scene-about');

        this.cards = [...this.el.querySelectorAll('.hero__advantages-card')];
        this.images = [...this.el.querySelectorAll('.hero__images .hero__image')];

        this.mask = this.el.querySelector('.hero__mask');
        this.maskImages = this.mask ? [...this.mask.children] : [];
        this.overlay = this.el.querySelector('.hero__overlay');

        this.numbersWrapper = this.el.querySelector('.hero__about-numbers');
        this.numbers = [...this.el.querySelectorAll('.hero__about-number')];
        this.numbersObserver = null;
        this.numbersInterval = null;

        this.numberValues = [...this.el.querySelectorAll('.hero__about-number-value')];
        this.numbersTween = null;

        this.currentImageIndex = 0;
        this.cardOffset = 16;

        this.isLandscape = false;
        this.tl = null;
        this.st = null;


        this.init();
    }

    init() {
        this.initAccordion();
        this.initImages();

        this.handleResponsive();
        this.handleResize();
        this.initNumbersAnimation();
    }

    initAccordion() {
        const accordion = this.el.querySelector('.hero__info[data-accordion=accordion]');
        if (!accordion) return;

        new Accordion('.hero__info[data-accordion=accordion]', {
            default: 0,
        });
    }


    initImages() {
        if (!this.images.length) return;

        this.currentImageIndex = 0;

        this.images.forEach((image, index) => {
            image.classList.toggle('is-active', index === 0);
        });

        this.maskImages.forEach((image, index) => {
            image.classList.toggle('is-active', index === 0);
        });
    }

    setActiveImage(index) {
        if (!this.images.length) return;

        const maxIndex = this.images.length - 1;

        if (index < 0) index = 0;
        if (index > maxIndex) index = maxIndex;
        if (this.currentImageIndex === index) return;

        this.currentImageIndex = index;

        this.images.forEach((image, imageIndex) => {
            image.classList.toggle('is-active', imageIndex === index);
        });

        this.maskImages.forEach((image, imageIndex) => {
            image.classList.toggle('is-active', imageIndex === index);
        });
    }

    getCardY(index) {
        return index * this.cardOffset;
    }

    initScrollAnimation() {
        if (!this.introScene || !this.advantagesScene || !this.cards.length) return;

        gsap.set(this.advantagesScene, {
            yPercent: 100,
        });

        if (this.aboutScene) {
            gsap.set(this.aboutScene, {
                yPercent: 100,
            });
        }

        gsap.set(this.cards, {
            y: this.el.offsetHeight,
        });

        gsap.set(this.cards[0], {
            y: this.getCardY(0),
        });

        if (this.mask) {
            gsap.set(this.mask, {
                opacity: 0,
            });
        }

        if (this.overlay) {
            gsap.set(this.overlay, {
                opacity: 0,
            });
        }

        const scenesCount = this.cards.length + (this.aboutScene ? 1 : 0);

        this.tl = gsap.timeline({
            scrollTrigger: {
                trigger: this.el,
                start: 'top top',
                end: () => `+=${this.el.offsetHeight * scenesCount}`,
                scrub: true,
                pin: true,
                anticipatePin: 1,
                invalidateOnRefresh: true,
            },
        });

        this.tl.to(this.introScene, {
            y: () => -this.introScene.scrollHeight,
            ease: 'none',
            duration: 1,
        }, 0);

        this.tl.to(this.advantagesScene, {
            yPercent: 0,
            ease: 'none',
            duration: 1,
        }, 0);

        if (this.overlay) {
            this.tl.to(this.overlay, {
                opacity: 1,
                ease: 'none',
                duration: 0.8,
            }, 0.2);
        }

        if (this.mask) {
            this.tl.to(this.mask, {
                opacity: 1,
                ease: 'none',
                duration: 0.8,
            }, 0.2);
        }

        this.cards.forEach((card, index) => {
            if (index === 0) return;

            this.tl.to(card, {
                y: this.getCardY(index),
                ease: 'none',
                duration: 0.7,
                onComplete: () => {
                    this.setActiveImage(index);
                },
                onReverseComplete: () => {
                    this.setActiveImage(index - 1);
                },
            });
        });

        if (this.aboutScene) {
            this.tl.to(this.advantagesScene, {
                yPercent: -100,
                ease: 'none',
                duration: 1,
            });

            this.tl.to(this.aboutScene, {
                yPercent: 0,
                ease: 'none',
                duration: 1,
            }, '<');

            if (this.mask) {
                this.tl.to(this.mask, {
                    opacity: 0,
                    ease: 'none',
                    duration: 0.8,
                }, '<');
            }

            if (this.overlay) {
                this.tl.to(this.overlay, {
                    opacity: 0,
                    ease: 'none',
                    duration: 0.8,
                }, '<');
            }
        }
    }

    handleResponsive() {
        const isLandscape = this.isPhoneLandscape();

        if (isLandscape && !this.isLandscape) {
            this.destroyScroll();
            this.isLandscape = true;
            return;
        }

        if (!isLandscape && this.isLandscape) {
            this.destroyScroll();
            this.isLandscape = false;
            this.initScrollAnimation();

            requestAnimationFrame(() => {
                ScrollTrigger.refresh();
            });

            return;
        }

        if (!isLandscape && !this.tl) {
            this.initScrollAnimation();

            requestAnimationFrame(() => {
                ScrollTrigger.refresh();
            });
        }
    }

    handleResize() {
        let lastLandscape = this.isPhoneLandscape();

        window.addEventListener('resize', () => {
            const currentLandscape = this.isPhoneLandscape();

            if (currentLandscape === lastLandscape) return;

            lastLandscape = currentLandscape;
            this.handleResponsive();
        });
    }


    destroyScroll() {
        if (this.tl && this.tl.scrollTrigger) {
            this.tl.scrollTrigger.kill(true);
        }

        if (this.tl) {
            this.tl.kill();
            this.tl = null;
        }

        gsap.killTweensOf([
            this.introScene,
            this.advantagesScene,
            this.aboutScene,
            this.cards,
            this.mask,
            this.overlay,
        ]);

        gsap.set([
            this.el,
            this.inner,
            this.introScene,
            this.advantagesScene,
            this.aboutScene,
            this.cards,
            this.mask,
            this.overlay,
        ], {
            clearProps: 'all',
        });

        this.initImages();

        requestAnimationFrame(() => {
            ScrollTrigger.refresh();
        });
    }

    isPhoneLandscape() {
        return window.matchMedia('(max-width: 1024px) and (orientation: landscape)').matches;
    }

    initNumbersAnimation() {
        if (!this.numbers.length || !this.numbersWrapper) return;

        this.numbersObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.startNumbersLoop();
                    } else {
                        this.stopNumbersLoop();
                    }
                });
            },
            {
                threshold: 0.5,
            }
        );

        this.numbersObserver.observe(this.numbersWrapper);
    }

    startNumbersLoop() {
        if (this.numbersTween) return;

        this.playNumbers();
    }

    stopNumbersLoop() {
        if (this.numbersTween) {
            this.numbersTween.kill();
            this.numbersTween = null;
        }

        this.resetNumbers();
    }

    playNumbers() {
        const state = {
            progress: 0,
        };

        if (this.numbersTween) {
            this.numbersTween.kill();
            this.numbersTween = null;
        }

        this.resetNumbers();

        this.numbersTween = gsap.to(state, {
            progress: 1,
            duration: 3,
            ease: 'none',
            repeat: -1,
            repeatDelay: 4,
            onUpdate: () => {
                this.updateNumbersFill(state.progress);
            },
            onRepeat: () => {
                this.resetNumbers();
            },
        });
    }

    updateNumbersFill(progress) {
        if (!this.numberValues.length) return;

        const items = this.numberValues.map((item) => {
            const rect = item.getBoundingClientRect();

            return {
                el: item,
                width: rect.width,
            };
        });

        const totalWidth = items.reduce((sum, item) => sum + item.width, 0);
        const frontX = (progress * 2 - 0.5) * totalWidth;

        let passedWidth = 0;

        items.forEach((item) => {
            const localX = frontX - passedWidth;
            const percent = (localX / item.width) * 100;

            item.el.style.setProperty('--fill-progress', `${percent}%`);

            passedWidth += item.width;
        });
    }

    resetNumbers() {
        this.numberValues.forEach((item) => {
            item.style.setProperty('--fill-progress', '-120%');
        });
    }
}